<script setup>
import { ref, watch, computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { CATEGORIES, STATUSES, PRIORITIES } from '../utils/taskMeta';
import FilterSelect from '../../socialMonitoring/components/FilterSelect.vue';

const props = defineProps({
  open: { type: Boolean, default: false },
  task: { type: Object, default: null },
  saving: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'save', 'delete']);

const form = ref({
  title: '',
  description: '',
  category: 'operacional',
  status: 'pendente',
  priority: 'media',
  dueDate: '',
  checklist: [],
});
const newChecklistItem = ref('');

const isEdit = computed(() => !!props.task?.id);

watch(
  () => [props.open, props.task],
  () => {
    if (!props.open) return;
    if (props.task) {
      form.value = {
        title: props.task.title || '',
        description: props.task.description || '',
        category: props.task.category || 'operacional',
        status: props.task.status || 'pendente',
        priority: props.task.priority || 'media',
        dueDate: props.task.dueDate ? props.task.dueDate.slice(0, 10) : '',
        checklist: [...(props.task.checklist || [])],
      };
    } else {
      form.value = {
        title: '',
        description: '',
        category: 'operacional',
        status: 'pendente',
        priority: 'media',
        dueDate: '',
        checklist: [],
      };
    }
  },
  { immediate: true }
);

function addChecklist() {
  const text = newChecklistItem.value.trim();
  if (!text) return;
  form.value.checklist.push({ text, done: false });
  newChecklistItem.value = '';
}
function removeChecklist(i) {
  form.value.checklist.splice(i, 1);
}

function submit() {
  const payload = { ...form.value };
  if (props.task?.id) payload.id = props.task.id;
  emit('save', payload);
}

const catOptions = CATEGORIES.map(c => ({
  value: c.value,
  label: c.label,
  dot: c.dot,
  icon: c.icon,
}));
const statusOptions = STATUSES.map(s => ({ value: s.value, label: s.label }));
const priorityOptions = PRIORITIES.map(p => ({
  value: p.value,
  label: p.label,
}));
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="emit('close')"
    >
      <div
        class="absolute inset-0 bg-black/50 backdrop-blur-sm"
        @click="emit('close')"
      />
      <div
        class="relative z-10 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-n-background shadow-2xl"
      >
        <div
          class="flex items-center justify-between border-b border-n-weak px-5 py-3"
        >
          <h3 class="text-base font-bold text-n-slate-12">
            {{ isEdit ? 'Editar tarefa' : 'Nova tarefa' }}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            icon="i-lucide-x"
            @click="emit('close')"
          />
        </div>

        <div class="flex-1 overflow-y-auto p-5">
          <div class="space-y-4">
            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Título</label>
              <input
                v-model="form.title"
                type="text"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm text-n-slate-12"
              />
            </div>

            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Descrição</label>
              <textarea
                v-model="form.description"
                rows="3"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm text-n-slate-12"
              />
            </div>

            <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <label class="mb-1 block text-xs font-semibold text-n-slate-11">Categoria</label>
                <FilterSelect
                  v-model="form.category"
                  :options="catOptions"
                  icon="i-lucide-layers"
                  min-width="100%"
                />
              </div>
              <div>
                <label class="mb-1 block text-xs font-semibold text-n-slate-11">Status</label>
                <FilterSelect
                  v-model="form.status"
                  :options="statusOptions"
                  min-width="100%"
                />
              </div>
              <div>
                <label class="mb-1 block text-xs font-semibold text-n-slate-11">Prioridade</label>
                <FilterSelect
                  v-model="form.priority"
                  :options="priorityOptions"
                  min-width="100%"
                />
              </div>
            </div>

            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Prazo</label>
              <input
                v-model="form.dueDate"
                type="date"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm text-n-slate-12"
              />
            </div>

            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Checklist</label>
              <div class="mb-2 flex gap-2">
                <input
                  v-model="newChecklistItem"
                  type="text"
                  placeholder="Adicionar item..."
                  class="flex-1 rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
                  @keydown.enter.prevent="addChecklist"
                />
                <Button
                  size="sm"
                  variant="outline"
                  icon="i-lucide-plus"
                  @click="addChecklist"
                >
                  Adicionar
                </Button>
              </div>
              <ul v-if="form.checklist.length" class="space-y-1">
                <li
                  v-for="(item, i) in form.checklist"
                  :key="i"
                  class="flex items-center gap-2 rounded-md bg-n-slate-2 px-2 py-1.5"
                >
                  <input
                    v-model="item.done"
                    type="checkbox"
                    class="h-4 w-4 accent-blue-600"
                  />
                  <span
                    class="flex-1 text-sm"
                    :class="[
                      item.done
                        ? 'text-n-slate-11 line-through'
                        : 'text-n-slate-12',
                    ]"
                  >
                    {{ item.text }}
                  </span>
                  <button
                    type="button"
                    class="text-n-slate-11 hover:text-rose-600"
                    @click="removeChecklist(i)"
                  >
                    <span class="i-lucide-x size-3.5" />
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div
          class="flex items-center justify-between border-t border-n-weak px-5 py-3"
        >
          <Button
            v-if="isEdit"
            variant="ghost"
            size="sm"
            icon="i-lucide-trash-2"
            color="ruby"
            @click="emit('delete', task)"
          >
            Excluir
          </Button>
          <span v-else />
          <div class="flex gap-2">
            <Button variant="outline" size="sm" @click="emit('close')">
              Cancelar
            </Button>
            <Button
              variant="solid"
              color="blue"
              size="sm"
              :is-loading="saving"
              :disabled="!form.title.trim()"
              @click="submit"
            >
              {{ isEdit ? 'Salvar' : 'Criar' }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
